import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc, onSnapshot, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import PickupStep from './PickupStep';
import DeliveryStep from './DeliveryStep';
import MassIcon from './MassIcon';
import OrderSteps from './OrderSteps';
import LateAdditions from './LateAdditions';
import ClaimsPanel from './ClaimsPanel';
import ReceiptsPanel from './ReceiptsPanel';
import OrderTimeline from '../common/OrderTimeline';
import { useOrderTimelineData } from '../../hooks/useOrderTimelineData';
import { buildOrderTimeline } from '../../utils/orderTimeline';
import { orderTitle } from '../../utils/orderTitle';
import { orderHourlyText } from '../../utils/orderHourly';
import { massAccountLines } from '../../utils/massAccount';
import { isCarrierOrder, shouldShareLocation, withoutAgreedPrice } from '../../utils/carrierOrder';
import CarrierOrderInfo from './CarrierOrderInfo';
import { useAuth } from '../../contexts/AuthContext';
import { usePricingSettings } from '../../hooks/usePricingSettings';
import { calculateBreakdown, haversineKm } from '../../utils/priceCalculator';
import { fullAddress, timeLabel, dayLabel, toDate } from '../../utils/orderCardFormat';
import { stripeApi } from '../../firebase/functionsService';
import { COLORS, FONTS } from '../../styles/tokens';
import { TruckLoader, TruckDriving, TruckAnimation, useTruckTheme } from '../common/truck';
import { hasCurrentLocationConsent } from '../../utils/locationConsent';
import { useWakeLock } from '../../hooks/useWakeLock';
import OrdererPhotos from '../common/OrdererPhotos';

// GPS gir nye punkter omtrent hvert sekund i bevegelse; skriv sjeldnere for å spare Firestore-skriv
const LOCATION_WRITE_INTERVAL_MS = 15000;

// Et tildelt oppdrag (henting → levering → bekreftelse), vist i arket over kartet på mobil
// og i sidepanelet på desktop. onClose lukker panelet og går tilbake til listene.
export default function ActiveOrderPanel({ orderId, onClose }) {
  const [activeOrder, setActiveOrder] = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    const unsubscribe = onSnapshot(doc(db, 'orders', orderId), (snap) => {
      setActiveOrder(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    }, () => {
      // Avvist av reglene (f.eks. ukjent id eller annen sjåførs oppdrag) — vis «Fant ikke»
      setActiveOrder(null);
      setLoading(false);
    });
    return unsubscribe;
  }, [orderId]);

  const { baseRate } = usePricingSettings();
  const [fuelType, setFuelType] = useState('diesel');
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const { currentUser, userDoc } = useAuth();
  const canShareLocation = hasCurrentLocationConsent(userDoc);

  useEffect(() => {
    if (!currentUser?.uid) return;
    getDocs(query(collection(db, 'vehicles'), where('driverUid', '==', currentUser.uid)))
      .then(snap => {
        if (!snap.empty) setFuelType(snap.docs[0].data().fuelType ?? 'diesel');
      })
      .catch(() => {});
  }, [currentUser?.uid]);

  // Estimert jobbverdi gjelder markedsplassen. Egne ordre har ingen prisberegning for sjåføren.
  // Timeoppdrag og ordre uten bestilt mengde har ingen avstandsbasert beregning.
  const breakdown = !isCarrierOrder(activeOrder) && !activeOrder?.hourly && activeOrder?.estimatedTons > 0
    && activeOrder?.pickupAddress?.lat && activeOrder?.deliveryAddress?.lat
    ? calculateBreakdown(
        haversineKm(
          activeOrder.pickupAddress.lat, activeOrder.pickupAddress.lng,
          activeOrder.deliveryAddress.lat, activeOrder.deliveryAddress.lng,
        ),
        activeOrder.massType,
        activeOrder.estimatedTons,
        fuelType,
        baseRate,
      )
    : null;

  const truckTheme = useTruckTheme();
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  async function handleCancel() {
    if (!window.confirm(
      'Er du sikker på at du vil kansellere oppdraget?\n\nGjentatte kanselleringer etter tildeling vil påvirke din sjåfør-score negativt.'
    )) return;
    setCancelling(true);
    setCancelError('');
    try {
      await stripeApi.cancelAcceptedOrder(activeOrder.id);
      onClose();
    } catch (err) {
      setCancelError('Kunne ikke kansellere. Prøv igjen.');
      setCancelling(false);
    }
  }

  // Del posisjonen med bestilleren fra tildeling til levering, så lenge sjåføren har godtatt
  // gjeldende samtykketekst. Nettlesere stopper GPS når skjermen låses eller appen byttes,
  // så oppdateringene skjer bare mens dette panelet er åpent.
  const orderIdForLocation = activeOrder?.id;
  const shareLocationNow = shouldShareLocation(activeOrder, canShareLocation);
  useEffect(() => {
    if (!orderIdForLocation || !shareLocationNow || !navigator.geolocation) return;

    let lastWrite = 0;
    const watchId = navigator.geolocation.watchPosition(pos => {
      const now = Date.now();
      if (now - lastWrite < LOCATION_WRITE_INTERVAL_MS) return;
      lastWrite = now;
      updateDoc(doc(db, 'orders', orderIdForLocation), {
        // updatedAt ligger inne i feltet så sikkerhetsreglene (som bare tillater driverLocation) ikke må endres
        driverLocation: { lat: pos.coords.latitude, lng: pos.coords.longitude, updatedAt: serverTimestamp() },
      }).catch(() => {});
    }, () => {}, { enableHighAccuracy: true });

    return () => navigator.geolocation.clearWatch(watchId);
  }, [orderIdForLocation, shareLocationNow]);

  // Hold skjermen våken mens posisjonen deles, så sporingen ikke stopper når telefonen
  // står i holderen og ellers ville låst seg selv
  useWakeLock(shareLocationNow);

  // Tidslinje med det som er registrert så langt (henting, bilder, veieseddel, bud)
  const history = useOrderTimelineData(orderId, 'driver', !!orderId);
  const events = useMemo(
    () => (activeOrder ? buildOrderTimeline({ order: withoutAgreedPrice(activeOrder), bids: history.bids, invoice: history.invoice, creditNotes: history.creditNotes, role: 'driver' }) : []),
    [activeOrder, history.bids, history.invoice, history.creditNotes]
  );

  if (loading) {
    return <div style={styles.state}><TruckLoader showLabel label="Laster oppdrag…" /></div>;
  }

  if (!activeOrder) {
    return <div style={styles.state}>Fant ikke oppdraget</div>;
  }

  const isPickup          = activeOrder.status === 'matched';
  const isDelivery        = activeOrder.status === 'in_progress';
  const isPendingConfirm  = activeOrder.status === 'delivered_pending';
  const isCompleted       = activeOrder.status === 'completed';
  const scheduled         = toDate(activeOrder.scheduledAt);
  const time              = timeLabel(scheduled);
  const details = [orderHourlyText(activeOrder)].filter(Boolean);
  const accountLines = massAccountLines(activeOrder);
  const navTarget = isPickup ? activeOrder.pickupAddress : isDelivery ? activeOrder.deliveryAddress : null;

  return (
    <div style={styles.scroll}>
      {isDelivery && (
        <div aria-hidden="true" style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
          <TruckDriving size={88} theme={truckTheme} />
        </div>
      )}
      {/* Oppdragsinfo */}
      <div style={styles.summary}>
        <div style={styles.summaryTop}>
          <div style={{ minWidth: 0 }}>
            <div style={styles.meta}>
              #{activeOrder.id.slice(-6)}{scheduled && ` · ${dayLabel(scheduled)}`}{time && ` ${time}`}
            </div>
            <h2 style={styles.title}>
              {orderTitle(activeOrder)}
            </h2>
            {details.length > 0 && <div style={styles.sub}>{details.join(' · ')}</div>}
          </div>
          <MassIcon massType={activeOrder.massType} />
        </div>
        <div style={styles.routeBox}>
          <div style={styles.stop}>
            <span style={styles.dotPickup} />
            <div style={{ minWidth: 0 }}>
              <div style={styles.stopLabel}>Henting</div>
              <div style={styles.stopAddr}>{fullAddress(activeOrder.pickupAddress)}</div>
            </div>
          </div>
          <div style={styles.stop}>
            <span style={styles.dotDelivery} />
            <div style={{ minWidth: 0 }}>
              <div style={styles.stopLabel}>Levering</div>
              <div style={styles.stopAddr}>{fullAddress(activeOrder.deliveryAddress)}</div>
            </div>
          </div>
        </div>
        {accountLines.length > 0 && (
          <div style={styles.accountBox}>
            {accountLines.map(l => (
              <div key={l.label}>
                <div style={styles.stopLabel}>{l.label}</div>
                <div style={l.label === 'Merk med' ? styles.markWith : styles.stopAddr}>{l.value}</div>
              </div>
            ))}
          </div>
        )}
        <OrdererPhotos order={activeOrder} />
        {navTarget && (
          <a href={navUrl(navTarget)} target="_blank" rel="noopener noreferrer" style={styles.navBtn}>
            Navigér til {isPickup ? 'henting' : 'levering'}
          </a>
        )}
      </div>

      <CarrierOrderInfo order={activeOrder} />

      {/* Stegindikator */}
      <div style={{ marginBottom: 20 }}><OrderSteps status={activeOrder.status} /></div>

      {cancelError && <p style={{ background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 14px', borderRadius: 6, fontSize: 14, marginBottom: 12 }}>{cancelError}</p>}

      {/* Ankomst, ventetid og bomtur gjelder bare markedsplassen; egne ordre har ikke bestiller som godkjenner */}
      {!isCarrierOrder(activeOrder) && <ClaimsPanel order={activeOrder} />}

      {/* Billag (veiesedler, kvitteringer) fra tildelt til levert; panelet skjuler seg selv ellers */}
      {!isCarrierOrder(activeOrder) && <ReceiptsPanel order={activeOrder} />}

      {/* Aktivt steg */}
      {isPickup && (
        <>
          <h2 style={styles.stepTitle}>Registrer henting</h2>
          <PickupStep order={activeOrder} />
          {breakdown && (
            <div style={{ marginTop: 16, background: COLORS.warningBg, border: `1px solid ${COLORS.warningBorder}`, borderRadius: 8, padding: '12px 14px' }}>
              <button
                onClick={() => setBreakdownOpen(o => !o)}
                aria-expanded={breakdownOpen}
                style={{ display: 'flex', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 0, color: COLORS.textDark }}
              >
                <span>Estimert jobbverdi: <strong>kr {breakdown.total},-</strong></span>
                <span style={{ fontSize: 12, color: COLORS.textLight }}>{breakdownOpen ? '▲ Skjul' : '▼ Detaljer'}</span>
              </button>
              {breakdownOpen && (
                <table style={{ width: '100%', fontSize: 12, marginTop: 8, borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr><td>Distanse ({breakdown.distanceKm.toFixed(1)} km × {breakdown.baseRate} kr)</td><td style={{ textAlign: 'right' }}>kr {breakdown.distanceBase},-</td></tr>
                    {breakdown.massSurcharge > 0 && <tr><td>Lasttype-tillegg</td><td style={{ textAlign: 'right' }}>kr {breakdown.massSurcharge},-</td></tr>}
                    {breakdown.weightSurcharge > 0 && <tr><td>Vekttillegg</td><td style={{ textAlign: 'right' }}>kr {breakdown.weightSurcharge},-</td></tr>}
                    <tr><td>Bompenger (estimat)</td><td style={{ textAlign: 'right' }}>ca. kr {breakdown.toll},-</td></tr>
                    <tr><td>Drivstoff ({breakdown.fuelType})</td><td style={{ textAlign: 'right' }}>kr {breakdown.fuel},-</td></tr>
                    <tr style={{ fontWeight: 700, borderTop: `1px solid ${COLORS.warningBorder}` }}><td>Total</td><td style={{ textAlign: 'right' }}>kr {breakdown.total},-</td></tr>
                  </tbody>
                </table>
              )}
            </div>
          )}
          {/* Egne ordre kanselleres av dispatcher, ikke av sjåføren */}
          {!isCarrierOrder(activeOrder) && (
            <button
              style={{ marginTop: 20, width: '100%', padding: '12px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.white, fontSize: 14, color: COLORS.textLight, cursor: 'pointer' }}
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? 'Kansellerer…' : 'Kanseller oppdrag'}
            </button>
          )}
        </>
      )}

      {isDelivery && (
        <>
          <h2 style={styles.stepTitle}>Registrer levering</h2>
          <DeliveryStep order={activeOrder} />
        </>
      )}

      {isPendingConfirm && (() => {
        const deadline = activeOrder.deliveryConfirmDeadline?.toDate?.();
        const deliveredAt = activeOrder.deliveredAt?.toDate?.();
        return (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Venter på bekreftelse</h2>
            <p style={{ color: COLORS.textMid, fontSize: 14, marginBottom: 16 }}>
              Bestiller har 24 timer på å bekrefte leveringen.
              Betaling utbetales automatisk ved godkjenning.
            </p>
            {deliveredAt && (
              <p style={{ fontSize: 13, color: COLORS.textLight, margin: '4px 0' }}>
                Levert: {deliveredAt.toLocaleString('nb-NO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
            {deadline && (
              <p style={{ fontSize: 13, color: COLORS.textLight, margin: '4px 0' }}>
                Auto-godkjenning: {deadline.toLocaleString('nb-NO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        );
      })()}

      {isCompleted && (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <TruckAnimation variant="success" theme={truckTheme} size={110} decorative />
          <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Levering fullført</h2>
          <p style={{ color: COLORS.textMid, fontSize: 14 }}>
            {isCarrierOrder(activeOrder)
              ? 'Dokumentasjonen er lagret, og dispatcher er varslet.'
              : 'Dokumentasjonen er lagret. Faktura genereres automatisk.'}
          </p>
        </div>
      )}

      <OrderTimeline events={events} role="driver" loading={history.loading} />
      <LateAdditions order={activeOrder} />
    </div>
  );
}

// Toppen av arket/sidepanelet mens et oppdrag er åpent
export function ActiveOrderPanelHeader({ onClose }) {
  return (
    <div style={styles.header}>
      <button onClick={onClose} style={styles.backBtn}>← Tilbake</button>
      <span style={styles.headerTitle}>Mitt oppdrag</span>
    </div>
  );
}

function navUrl(address) {
  const dest = address?.lat != null ? `${address.lat},${address.lng}` : (address?.street ?? '');
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
}

const styles = {
  scroll:      { flex: 1, overflowY: 'auto', padding: '12px 16px 32px', background: COLORS.bg },
  state:       { padding: 40, textAlign: 'center', color: COLORS.textLight },
  header:      { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' },
  backBtn:     { background: 'none', border: 'none', color: COLORS.textMid, fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0 },
  headerTitle: { fontFamily: FONTS.display, fontSize: 22, fontWeight: 700, color: COLORS.textDark },
  summary:     { background: COLORS.white, borderRadius: 18, padding: 16, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12, border: `1px solid ${COLORS.border}` },
  summaryTop:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  meta:        { fontFamily: 'var(--font-mono)', fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: 0.3 },
  title:       { fontFamily: FONTS.display, fontSize: 30, lineHeight: 1.05, fontWeight: 700, color: COLORS.textDark, margin: '4px 0 2px' },
  sub:         { fontSize: 13, color: COLORS.textMid },
  routeBox:    { background: COLORS.bg, borderRadius: 14, padding: 12, display: 'flex', flexDirection: 'column', gap: 12 },
  stop:        { display: 'flex', alignItems: 'flex-start', gap: 10 },
  stopLabel:   { fontSize: 11, color: COLORS.textLight },
  stopAddr:    { fontSize: 15, fontWeight: 600, color: COLORS.textDark },
  dotPickup:   { width: 10, height: 10, borderRadius: '50%', background: COLORS.primary, marginTop: 11, flexShrink: 0 },
  dotDelivery: { width: 12, height: 12, borderRadius: '50%', background: COLORS.accent, border: `2px solid ${COLORS.primary}`, marginTop: 10, flexShrink: 0, boxSizing: 'border-box' },
  accountBox:  { background: COLORS.bg, borderRadius: 14, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 },
  markWith:    { fontSize: 18, fontWeight: 700, color: COLORS.textDark },
  navBtn:      { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 48, borderRadius: 24, border: `2px solid ${COLORS.primary}`, color: COLORS.textDark, fontWeight: 700, fontSize: 15, textDecoration: 'none' },
  stepBar:     { display: 'flex', alignItems: 'flex-start', marginBottom: 20, padding: '0 4px' },
  line:        { flex: 1, height: 2, margin: '14px 4px 0' },
  stepTitle:   { fontFamily: FONTS.display, fontSize: 26, fontWeight: 700, color: COLORS.textDark, margin: '0 0 12px' },
};
